import { booleanAttribute, ChangeDetectionStrategy, Component, inject, Input, input, OnChanges, OnInit, output, signal, SimpleChanges, viewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { PanelModule } from 'primeng/panel';
import { HisCoreSharedModule } from '../../../core/his-core-shared.module';
import { HisUiService } from '../../../core/service/base/his-ui.service';
import { HisNotificationService } from '../../../core/service/notifications/his-notification.service';
import { intersection } from '../../../core/utils';
import { HisTableContextDirective } from '../../directive/his-table-context.directive';
import { calculateColumnOrder } from '../../model/table-column-helper';
import { DevTableConfigValues, TableColumnConfig, TableConfig } from '../../model/table-model';

/** Internal UI model used for the template binding. Decoupled from any external models */
interface UiModel {
    columnConfigs: TableColumnConfig[];
    frozenCol: string;
    naviBefore: boolean;
    naviAfter: boolean;
    defaultTablePageSize: number;
    columnsReorderable: boolean;
}

/** Compatibility type helper for easier integration of different legacy data models */
type TableColumnVariant = { id?: string; label?: string; title?: string; header?: string; visible?: boolean; checked?: boolean; };

/** Converts a TableColumnVariant to a unified TableColumnConfig */
function toUnifiedColumnConfig( columnVariant: TableColumnVariant, index: number ): TableColumnConfig {
    return {
        ...columnVariant,
        id: columnVariant.id ?? `column_${index}`,
        label: columnVariant.label ?? columnVariant.title ?? columnVariant.header
    } as TableColumnConfig;
}

/** Makes the unified TableColumnConfig compatible with older data models */
function toGenericColumnMeta( columnConfig: TableColumnConfig ): TableColumnConfig {
    return {
        ...columnConfig,
        id: columnConfig.id,
        label: columnConfig.label,
        title: columnConfig.label,
        visible: columnConfig.visible,
        checked: columnConfig.visible
    };
}

@Component({
    selector: 'his-table-config',
    templateUrl: './his-table-config.component.html',
    standalone: true,
    imports: [HisCoreSharedModule, DialogModule, PanelModule, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HisTableConfigComponent implements OnChanges, OnInit {
    private readonly context = inject( HisTableContextDirective );
    private readonly uiService = inject( HisUiService );
    private readonly notificationService = inject( HisNotificationService );

    public readonly saveButtonLabel = input( 'common.apply' );
    public readonly cancelButtonLabel = input( 'common.cancel' );
    public readonly resetButtonLabel = input( 'resetToDefault' );
    public readonly maxRows = input( 300 );
    public readonly columns = input<TableColumnConfig[], TableColumnVariant[]>( [], { transform: ( columns ) => columns.map( toUnifiedColumnConfig ) } );

    /** Hides the reset button from the overlay */
    public readonly disableResetButton = input( false, { transform: booleanAttribute } );
    /** Disables the persistence of the configuration in the userProfile (or sessionStorage) via the `TableContextDirective`. */
    public readonly disablePersist = input( false, { transform: booleanAttribute } );
    /** Enables frozen columns feature in the table configuration. */
    public readonly enableFrozenColumns = input( false, { transform: booleanAttribute } );
    /** Enables paginator configuration in the table configuration. */
    public readonly enablePaginatorConfig = input( false, { transform: booleanAttribute } );
    /** Enables reorderable columns feature in the table configuration. */
    public readonly enableReorderableColumns = input( false, { transform: booleanAttribute } );

    /** @deprecated legacy compatibility for non-GS table-configs; visibility will be determined solely by the `visible` property of the `columns`; **WILL BE REMOVED** */
    @Input() configuratedColumns: any[];

    /** Whether to emit the initial table configuration upon initialization. */
    public readonly enableInitialEmit = input( false, { transform: booleanAttribute } );
    /** Event emitted when the table configuration is saved. */
    public readonly saved = output<TableConfig>();
    
    protected readonly frozenColumnOptions = [
        { label: 'tableConfig.noneColumn', value: 'noneCol' },
        { label: 'tableConfig.firstColumn', value: '1' },
        { label: 'tableConfig.firstSecondColumn', value: '2' },
        { label: 'tableConfig.firstThreeColumns', value: '3' }
    ];
    
    /** Reference to the current table configuration signal from the context as the single source of truth */
    private readonly config = this.context.tableConfig;
    
    protected readonly minRows = 1;
    /** Internal UI model representing the current state of the table configuration dialog. Decoupled from the actual config to allow reset to previous state. */
    protected uiModel: UiModel;
    protected readonly tableConfigDialogVisible = signal( false );
    private readonly pageSizeModel = viewChild<NgModel>( 'pageSizeModel' );

    ngOnInit() {
        if ( this.enableInitialEmit() ) {
            this.saved.emit( this.parseTableConfig( this.config() ) );
        }
    }

    ngOnChanges( changes: SimpleChanges ) {
        if ( changes.configuratedColumns ) {
            // LEGACY patch:
            // Reset all columns to invisible, then set the visibility of the columns that are present in the configuratedColumns input.
            // This ensures that only the specified columns are visible in the configuration dialog.
            this.columns().forEach( c => c.visible = false );
            intersection( this.columns(), changes.configuratedColumns.currentValue, ( c1: TableColumnConfig, c2: any ) => c1.id === c2.id || c1.label === ( c2.title || c2.label ) ).forEach( c => c.visible = true );
        }
    }

    /** Shows the table configuration dialog. Initializes the UI model to the current state of the configuration. */
    public showTableConfigDialog(): void {
        this.uiModel = this.initUiModel();
        this.tableConfigDialogVisible.set( true );
    }

    /**
     * Initializes the internal UI model based on the input properties and persisted user configuration (if enabled).\
     * Each property has a hardcoded default as a last fallback, which can be overridden by developer defaults passed via the `defaultConfig`
     * input property (table-context-directive) and then by user configuration if `enablePersist` is true.
     */
    private initUiModel() {
        return this.createUiModel( this.config() );
    }

    private createUiModel( config: DevTableConfigValues ): UiModel {
        const columnConfigs = this.applyColumnConfigs( this.columns(), config );
        const naviBefore = config.paginatorPosition === 'top' || config.paginatorPosition === 'both';
        const naviAfter = config.paginatorPosition === 'bottom' || config.paginatorPosition === 'both';
        const { frozenCol, defaultTablePageSize, columnsReorderable } = config;
        return {
            columnConfigs,
            frozenCol,
            naviBefore,
            naviAfter,
            defaultTablePageSize,
            columnsReorderable
        };
    }

    private applyColumnConfigs( columns: TableColumnConfig[], config: Pick<DevTableConfigValues, 'hiddenCols' | 'colOrder'> = {hiddenCols: [], colOrder: null} ): TableColumnConfig[] {
        const { hiddenCols, colOrder } = config;
        const orderedColumns = colOrder ? calculateColumnOrder( columns.map( c => c.id ), colOrder ).map( id => columns.find( c => c.id === id ) ) : columns;

        // LEGACY PATCH //
        if ( this.configuratedColumns ) {
            return orderedColumns;
        }

        const hiddenColumnIdSet = new Set( hiddenCols );
        orderedColumns.forEach( c => c.visible = !hiddenColumnIdSet.has( c.id ) );
        return orderedColumns;
    }

    private hideTableConfigDialog(): void {
        this.notificationService.clear();
        this.tableConfigDialogVisible.set(false);
    }

    protected cancel() {
        this.hideTableConfigDialog();
    }

    protected async apply() {
        if ( !this.ensureUiModelValid() ) {
            return;
        }

        const config = this.parseUiModel( this.uiModel );
        await this.context.updateTableConfig( config, { persist: !this.disablePersist() } );
        this.emitSaved();
    }

    protected async reset() {
        await this.context.resetTableConfig( { persist: !this.disablePersist() } );
        this.emitSaved();
    }

    private emitSaved() {
        this.saved.emit( this.parseTableConfig( this.config() ) );
        this.hideTableConfigDialog();
    }

    /** Parses the internal DevTableConfig into the external TableConfig representation. Config has previously been updated. */
    private parseTableConfig( devConfig: DevTableConfigValues ): TableConfig {
        const { frozenCol, paginatorPosition, defaultTablePageSize, tablePageSize, columnsReorderable } = devConfig;
        const columnConfigs = this.applyColumnConfigs( this.columns(), devConfig ).map( toGenericColumnMeta );
        const config: TableConfig = {
            frozenCol,
            columnConfigs,
            paginatorPosition,
            defaultTablePageSize,
            tablePageSize,
            columnsReorderable,
            columResults: columnConfigs.filter( c => c.visible ), // previous version emitted only visible columns
            isReorderable: columnsReorderable
        };
        return config;
    }

    private parseUiModel( uiModel: UiModel ): Partial<DevTableConfigValues> {
        const devConf: Partial<DevTableConfigValues> = {
            frozenCol: uiModel.frozenCol,
            hiddenCols: uiModel.columnConfigs.filter( c => !c.visible ).map( c => c.id ),
            paginatorPosition: uiModel.naviBefore && uiModel.naviAfter ? 'both' : uiModel.naviBefore ? 'top' : 'bottom',
            defaultTablePageSize: +(uiModel.defaultTablePageSize) || null, // template uses type="text" to allow the validator to work properly; '' converts to 0, which becomes null for the config
            columnsReorderable: uiModel.columnsReorderable
        };
        if ( devConf.defaultTablePageSize !== null && devConf.defaultTablePageSize !== this.config().defaultTablePageSize ) {
            // if the global default was changed, immediately remove the local value to let it resolve to the global one later on
            // except if global default was removed (null), which preserves the local value as is
            devConf.tablePageSize = null;
        }
        return devConf;
    }

    private ensureUiModelValid() {
        if ( this.pageSizeModel() && this.pageSizeModel().invalid ) {
            const fieldLabel = this.uiService.translate( 'tableConfig.globalPageSizeDefault' );
            this.notificationService.errorI18n( 'de.his.faces.validator.NumberValidator.range', fieldLabel, this.minRows, this.maxRows() );
            return false;
        }
        return true;
    }
}